import React, { useState, useEffect, useRef } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Star, Send } from "lucide-react";



// ---------------------------------------------------------------------------
// Iteration 73 — Admin: manual selected-customers review email sender
// ---------------------------------------------------------------------------
const AdminReviewBackfillPanel = () => {
  const [query, setQuery] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({}); // {user_id: true}
  const [kind, setKind] = useState("session");  // "session" | "book"
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const pollTimer = useRef(null);
  useEffect(() => () => { if (pollTimer.current) clearInterval(pollTimer.current); }, []);

  // Debounced search for customers (re-uses the existing /admin/clients endpoint).
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/clients", {
          params: query.trim() ? { q: query.trim() } : {},
        });
        setCustomers(data.items || []);
      } catch {
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const toggle = (uid) => setSelected((s) => ({ ...s, [uid]: !s[uid] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const startPolling = (id) => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/reviews/backfill-progress/${id}`);
        setProgress(data);
        if (data.status === "done") {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
        }
      } catch {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    }, 5000);
  };

  const runSend = async () => {
    if (selectedIds.length === 0) return;
    setBusy(true);
    setProgress(null);
    try {
      const { data } = await api.post("/admin/reviews/send-manual", {
        user_ids: selectedIds,
        kind,
      });
      setProgress(data);
      toast.success(`بدء إرسال ${data.selected_count} رسالة على دفعات`);
      setConfirmOpen(false);
      setSelected({});
      if (data.status === "running" && data.id) startPolling(data.id);
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر إرسال رسائل التقييم"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-review-backfill" className="card-warm p-5" dir="rtl">
      <div className="flex items-center gap-2 mb-1">
        <Star className="w-5 h-5 text-[#0A4D3C]" />
        <h3 className="font-heading text-xl text-[#222222]">إرسال رابط التقييم يدويًا</h3>
      </div>
      <p className="text-sm text-[#5E5C58] leading-relaxed">
        النظام يُرسل التقييم تلقائيًا بعد 3 أيام من انتهاء الجلسة المؤكدة أو من
        تاريخ شراء الكتاب. هذه الأداة لإرسال يدوي مُحدد فقط للعملاء الذين تختارينهم
        من الأسفل. لا يتم الإرسال إلا للمحددين بعد التأكيد.
      </p>

      {/* Kind toggle */}
      <div className="mt-4 flex items-center gap-2">
        <span className="text-sm text-[#5E5C58]">نوع التقييم:</span>
        {[{key:"session", label:"تقييم جلسة"},{key:"book", label:"تقييم كتاب"}].map((k) => (
          <button
            key={k.key}
            type="button"
            onClick={() => setKind(k.key)}
            data-testid={`review-kind-${k.key}`}
            className={
              "rounded-full px-3 h-8 text-xs font-medium border transition " +
              (kind === k.key
                ? "bg-[#0A4D3C] text-white border-[#0A4D3C]"
                : "bg-white text-[#222222] border-[#E5E1D8] hover:bg-[#F6F4EE]")
            }
          >
            {k.label}
          </button>
        ))}
      </div>

      {/* Customer search */}
      <div className="mt-4 relative">
        <Search className="w-4 h-4 text-[#8B8780] absolute top-2.5 right-3" />
        <Input
          dir="rtl"
          data-testid="manual-review-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ابحثي بالاسم أو الإيميل أو رقم الجوال..."
          className="pr-9"
        />
      </div>

      {/* Customer list */}
      <div className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-[#E5E1D8]" data-testid="manual-review-customer-list">
        {loading ? (
          <div className="text-sm text-[#8B8780] text-center py-6">جاري التحميل...</div>
        ) : customers.length === 0 ? (
          <div className="text-sm text-[#8B8780] text-center py-6">لا يوجد عملاء مطابقون</div>
        ) : (
          customers.map((u) => (
            <label
              key={u.id}
              className="flex items-center gap-3 px-3 py-2 border-b border-[#E5E1D8] last:border-0 hover:bg-[#F6F4EE] cursor-pointer text-sm"
              data-testid={`manual-review-row-${u.id}`}
            >
              <input
                type="checkbox"
                checked={!!selected[u.id]}
                onChange={() => toggle(u.id)}
                disabled={!u.email}
                className="w-4 h-4 accent-[#0A4D3C]"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#222222] truncate">
                  {u.name || "—"} <span className="text-xs text-[#8B8780]">#{u.client_number || "—"}</span>
                </div>
                <div className="text-xs text-[#5E5C58] truncate" dir="ltr">
                  {u.email || "— لا يوجد بريد —"} · {u.phone || "—"}
                </div>
              </div>
            </label>
          ))
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          data-testid="manual-review-send-btn"
          onClick={() => setConfirmOpen(true)}
          disabled={busy || selectedIds.length === 0 || (progress && progress.status === "running")}
          className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
        >
          <Send className="w-3.5 h-3.5 ms-2" />
          {progress?.status === "running"
            ? "جاري الإرسال على دفعات..."
            : `إرسال للمحدّدين (${selectedIds.length})`}
        </Button>
        <span className="text-xs text-[#8B8780]" dir="ltr">batch_size=5 · delay=60s</span>
      </div>

      {/* Live progress */}
      {progress && (
        <div className="mt-4 rounded-xl bg-[#F6F4EE] p-4 text-sm" data-testid="manual-review-progress">
          <div className="flex items-center justify-between">
            <span className="font-heading text-base text-[#222222]">حالة الإرسال</span>
            <span className={
              progress.status === "done"
                ? "text-xs text-[#0A4D3C] font-semibold"
                : "text-xs text-[#B33939] font-semibold animate-pulse"
            }>
              {progress.status === "done" ? "اكتمل" : "جاري..."}
            </span>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
            <div className="text-[#5E5C58]">المحدّدون:</div>
            <div className="font-medium text-[#222222]" dir="ltr">{progress.selected_count}</div>
            <div className="text-[#5E5C58]">تم الإرسال:</div>
            <div className="font-semibold text-[#0A4D3C]" dir="ltr">{progress.sent}</div>
            <div className="text-[#5E5C58]">فشل الإرسال:</div>
            <div className={progress.failed ? "font-semibold text-[#B33939]" : "text-[#8B8780]"} dir="ltr">{progress.failed}</div>
            <div className="text-[#5E5C58]">حد Resend:</div>
            <div className={progress.rate_limit_hit ? "text-[#B33939] font-semibold" : "text-[#0A4D3C] font-semibold"}>
              {progress.rate_limit_hit ? "نعم — توقفنا" : "لا"}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          onClick={() => !busy && setConfirmOpen(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <div className="font-heading text-xl text-[#222222]">تأكيد الإرسال</div>
            <p className="text-sm text-[#222222] mt-3 leading-relaxed">
              سيتم إرسال رابط التقييم إلى العملاء المحددين فقط
              ({selectedIds.length} {selectedIds.length === 1 ? "عميلة" : "عملاء"})
              من نوع: <span className="font-semibold">{kind === "session" ? "تقييم جلسة" : "تقييم كتاب"}</span>.
              هل تريدين المتابعة؟
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setConfirmOpen(false)}
                disabled={busy}
              >
                إلغاء
              </Button>
              <Button
                onClick={runSend}
                disabled={busy}
                className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
                data-testid="manual-review-confirm"
              >
                {busy ? "جاري البدء..." : "نعم، أرسلي"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { AdminReviewBackfillPanel };
